import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../../config/constants';

import {
  CardContent,
  CardHeader,
  CardTitle,
} from "components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "components/ui/form";
import { Input } from "components/ui/input";
import { Button } from "components/ui/button";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Loader2 } from "lucide-react";
import { AuthWindow } from '../auth-window';
import { AuthTokens } from '../auth.types';
import { completeAuth } from '../complete-auth';
import { getAuthErrorMessage } from '../get-auth-error-message';
import { GoogleAuthButton } from '../google-auth-button';
import { user } from '../../../state/atoms/user.atom';
import { useAtom } from 'jotai';
import { useError } from '../../../context/ErrorContext';

const formSchema = z.object({
  email: z.string().email("Please enter a valid email address."),
  password: z.string().min(6, "Password must be at least 6 characters long."),
});

export default function SignUp() {
  const iframeRef = React.useRef<HTMLIFrameElement>(null);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = React.useState(false);
  const [, setUserData] = useAtom(user);
 const { setError } = useError();
 
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  // useEffect(() => {
  //   const handleIframeLoad = (event: MessageEvent) => {
  //     if (iframeRef.current) {
  //       if (event.data.accessToken && event.data.accessToken !== 'undefined') {
  //         new Promise((resolve) => {
  //           setTimeout(() => {
  //             resolve(true);
  //           }, 300);
  //         }).then(() => {
  //           localStorage.setItem('accessToken', event.data.accessToken);
  //           localStorage.setItem('refreshToken', event.data.refreshToken);
  //           setTimeout(() => {
  //             makeApiRequest('/v1/user', {
  //               method: 'GET',
  //               headers: {
  //                 'Authorization': `Bearer ${event.data.accessToken}`
  //               }
  //             }).then((userResponse) => {
  //               setUserData({
  //                 ...userResponse
  //               });
  //             });
  //           }, 1000);
  //           navigate('/settings');
  //         });
  //       }
  //     }
  //   };

  //   window.addEventListener('message', handleIframeLoad);
  //   return () => window.removeEventListener('message', handleIframeLoad);
  // }, [navigate]);

  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/v1/user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        setError(await getAuthErrorMessage(response, 'Email already in use'));
        throw new Error('Signup failed');
      }

      const result = await response.json();

      if (result.accessToken) {
        await completeAuth({
          tokens: {
            accessToken: result.accessToken,
            refreshToken: result.refreshToken,
          },
          setUserData,
          navigate,
        });
      }
    } catch (error) {
      console.error('Signup error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSuccess = async (tokens: AuthTokens) => {
    setIsLoading(true);
    try {
      await completeAuth({
        tokens,
        setUserData,
        navigate,
      });
    } catch (error) {
      console.error('Google signup error:', error);
      setError('Google sign-in failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <AuthWindow>
        <CardHeader className="space-y-2">
          <CardTitle className="text-3xl font-bold text-center">1browser</CardTitle>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="your@email.com"
                        type="email"
                        autoComplete="email"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex justify-between items-center">
                      <FormLabel>Password</FormLabel>
                    </div>
                    <FormControl>
                      <Input
                        type="password"
                        placeholder="••••••"
                        autoComplete="new-password"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button 
                type="submit" 
                className="w-full"
                disabled={isLoading}
              >
                {isLoading ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  'Sign up'
                )}
              </Button>

              <GoogleAuthButton
                disabled={isLoading}
                onSuccess={handleGoogleSuccess}
                onError={setError}
              />

              <div className="text-center text-sm">
                Already have an account?{' '}
                <Button 
                  variant="link" 
                  className="px-0"
                  onClick={() => navigate('/signin')}
                >
                  Sign in
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </AuthWindow>

      <iframe
        title="hiddenIframe"
        name="hiddenIframe"
        ref={iframeRef}
        style={{ display: 'none' }}
      />
    </>
  );
}
